'use client';
import React, { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import BlogNav from '@/components/blog/BlogNav';
import BlogCard, { BlogPost } from '@/components/blog/BlogCard';

async function fetchNotionPosts(): Promise<BlogPost[]> {
  const databaseId = process.env.NOTION_BLOG_DATABASE_ID;
  const apiKey = process.env.NOTION_API_KEY;

  if (!databaseId || !apiKey) return [];

  try {
    const res = await fetch(`https://api.notion.com/v1/databases/${databaseId}/query`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${apiKey}`,
        'Notion-Version': '2022-06-28',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filter: {
          property: 'Published',
          checkbox: { equals: true },
        },
        sorts: [
          {
            property: 'Publish Date',
            direction: 'descending',
          },
        ],
      }),
      next: { revalidate: 60 },
    });

    if (!res.ok) return [];

    const data = await res.json();

    return data.results.map((page: any, index: number) => {
      const props = page.properties;

      const title =
        props['Title']?.title?.[0]?.plain_text ?? 'Untitled';
      const category =
        props['Category']?.rich_text?.[0]?.plain_text ?? 'General';
      const thumbnail =
        props['Thumbnail URL']?.url ?? '';
      const publishDate =
        props['Publish Date']?.date?.start ?? null;
      const excerpt =
        props['Excerpt']?.rich_text?.[0]?.plain_text ?? '';

      const formattedDate = publishDate
        ? new Date(publishDate).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : '';

      return {
        id: index + 1,
        slug: page.id,
        title,
        excerpt,
        category,
        date: formattedDate,
        readTime: '',
        thumbnail,
        featured: index === 0,
      } as BlogPost;
    });
  } catch {
    return [];
  }
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const POSTS_PER_PAGE = 6;

  // Typing animation state
  const words = ['Visual', 'Artificial'];
  const [wordIndex, setWordIndex] = useState(0);
  const [displayed, setDisplayed] = useState('Visual');
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(5);

  useEffect(() => {
    const current = words[wordIndex];
    let timeout: ReturnType<typeof setTimeout>;

    if (!isDeleting && charIndex < current.length) {
      timeout = setTimeout(() => {
        setDisplayed(current.slice(0, charIndex + 1));
        setCharIndex((c) => c + 1);
      }, 100);
    } else if (!isDeleting && charIndex === current.length) {
      timeout = setTimeout(() => setIsDeleting(true), 1800);
    } else if (isDeleting && charIndex > 0) {
      timeout = setTimeout(() => {
        setDisplayed(current.slice(0, charIndex - 1));
        setCharIndex((c) => c - 1);
      }, 60);
    } else if (isDeleting && charIndex === 0) {
      const next = (wordIndex + 1) % words.length;
      setWordIndex(next);
      setIsDeleting(false);
    }

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, wordIndex]);

  useEffect(() => {
    fetch('/api/notion-posts')
      .then((r) => r.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
        } else {
          setPosts(data.posts ?? []);
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load posts.');
        setLoading(false);
      });
  }, []);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(posts.map((p) => p.category)));
    return ['All', ...cats];
  }, [posts]);

  const featuredPost = posts[0] ?? null;

  const filteredPosts = useMemo(() => {
    return posts.filter((post, idx) => {
      const matchesCategory =
        activeCategory === 'All' || post.category === activeCategory;
      return matchesCategory && idx !== 0;
    });
  }, [activeCategory, posts]);

  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);

  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  const handleCategoryChange = (cat: string) => {
    setActiveCategory(cat);
    setCurrentPage(1);
  };

  return (
    <div
      className="min-h-screen relative overflow-x-hidden"
      style={{ background: '#242424', fontFamily: "'Inter', sans-serif" }}>

      {/* ── Background Glows ─────────────────────────────────────────────── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          className="absolute rounded-full"
          style={{
            width: '700px',
            height: '700px',
            top: '-200px',
            right: '-150px',
            background: 'radial-gradient(circle, rgba(229,134,33,0.12) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'glowPulse 6s ease-in-out infinite',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '600px',
            height: '600px',
            bottom: '10%',
            left: '-100px',
            background: 'radial-gradient(circle, rgba(224,68,43,0.1) 0%, transparent 70%)',
            filter: 'blur(60px)',
            animation: 'glowPulse 8s ease-in-out infinite 2s',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: '500px',
            height: '500px',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(229,134,33,0.06) 0%, transparent 70%)',
            filter: 'blur(80px)',
          }}
        />
      </div>

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      <BlogNav />

      {/* ── Hero Header ──────────────────────────────────────────────────── */}
      <header className="relative pt-32 pb-16 px-6 text-left">
        <div className="max-w-4xl mx-auto">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ background: 'rgba(229,134,33,0.1)', border: '1px solid rgba(229,134,33,0.25)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#E58621] animate-pulse" />
            <span
              className="text-xs font-semibold tracking-widest uppercase text-[#E58621]"
              style={{ fontFamily: "'Inter', sans-serif" }}>
              Amaze Insights
            </span>
          </div>

          <h1
            className="text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6"
            style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 800 }}>
            The{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #E58621, #E0442B)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                display: 'inline-block',
                minWidth: '9ch',
              }}>
              {displayed}
              <span
                style={{
                  WebkitTextFillColor: '#E58621',
                  opacity: 1,
                  animation: 'blink 0.7s step-end infinite',
                }}>
                |
              </span>
            </span>{' '}
            Intelligence Blog
          </h1>

          <p
            className="text-lg md:text-xl max-w-2xl mb-10"
            style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter', sans-serif", lineHeight: '1.7' }}>
            Insights on AI-powered visual transformation, agency growth strategies, and the future of
            product imagery — straight from the Amaze team.
          </p>
        </div>
      </header>

      {/* ── Main Content ─────────────────────────────────────────────────── */}
      <main className="relative max-w-7xl mx-auto px-6 lg:px-12 pb-24">

        {/* ── Loading State ─────────────────────────────────────────────── */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div
              className="w-12 h-12 rounded-full border-2 border-t-[#E58621] border-[rgba(255,255,255,0.1)] animate-spin"
            />
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}>
              Loading amazing insights...
            </p>
          </div>
        )}

        {/* ── Error State ───────────────────────────────────────────────── */}
        {!loading && error && (
          <div
            className="flex flex-col items-center justify-center py-24 gap-4 rounded-2xl"
            style={{ background: 'rgba(224,68,43,0.06)', border: '1px solid rgba(224,68,43,0.2)' }}>
            <svg className="w-10 h-10" style={{ color: '#E0442B' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <p className="text-base font-semibold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Could not load posts
            </p>
            <p className="text-sm text-center max-w-sm" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}>
              {error}
            </p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ── Featured Post ──────────────────────────────────────────── */}
            {!activeCategory === 'All' && featuredPost && (
              <section className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className="h-px flex-1"
                    style={{ background: 'linear-gradient(to right, rgba(229,134,33,0.4), transparent)' }}
                  />
                  <span
                    className="text-xs font-semibold tracking-widest uppercase text-[#E58621]"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    Featured Article
                  </span>
                  <div
                    className="h-px flex-1"
                    style={{ background: 'linear-gradient(to left, rgba(229,134,33,0.4), transparent)' }}
                  />
                </div>

                <Link href={`/blog/${featuredPost.slug}`} className="group block">
                  <article
                    className="relative overflow-hidden rounded-[24px] transition-all duration-300 group-hover:scale-[1.005]"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                    }}>
                    <div
                      className="absolute inset-0 rounded-[24px] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      style={{
                        padding: '1px',
                        background: 'linear-gradient(135deg, #E58621, #E0442B)',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                      }}
                    />
                    <div className="grid md:grid-cols-2 gap-0">
                      <div className="relative overflow-hidden" style={{ minHeight: '320px' }}>
                        {featuredPost.thumbnail ? (
                          <img
                            src={featuredPost.thumbnail}
                            alt={featuredPost.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            style={{ position: 'absolute', inset: 0 }}
                          />
                        ) : (
                          <div
                            className="absolute inset-0"
                            style={{ background: 'linear-gradient(135deg, rgba(229,134,33,0.2), rgba(224,68,43,0.2))' }}
                          />
                        )}
                        <div
                          className="absolute inset-0"
                          style={{ background: 'linear-gradient(to right, transparent 60%, rgba(36,36,36,0.8))' }}
                        />
                        <span
                          className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold text-white tracking-wide uppercase"
                          style={{ background: '#E0442B', fontFamily: "'Inter', sans-serif" }}>
                          {featuredPost.category}
                        </span>
                        <span
                          className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase"
                          style={{
                            background: 'rgba(229,134,33,0.2)',
                            border: '1px solid rgba(229,134,33,0.4)',
                            color: '#E58621',
                            fontFamily: "'Inter', sans-serif",
                          }}>
                          ★ Featured
                        </span>
                      </div>
                      <div className="flex flex-col justify-center p-8 lg:p-12">
                        <div className="flex items-center gap-3 mb-4">
                          <span
                            className="text-sm font-medium"
                            style={{ color: '#E58621', fontFamily: "'Inter', sans-serif" }}>
                            {featuredPost.date}
                          </span>
                        </div>
                        <h2
                          className="text-2xl lg:text-3xl font-bold text-white mb-4 leading-snug group-hover:text-[#E58621] transition-colors duration-200"
                          style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}>
                          {featuredPost.title}
                        </h2>
                        <p
                          className="text-base leading-relaxed mb-6"
                          style={{ color: 'rgba(255,255,255,0.5)', fontFamily: "'Inter', sans-serif" }}>
                          {featuredPost.excerpt}
                        </p>
                        <span
                          className="inline-flex items-center gap-2 text-sm font-semibold"
                          style={{ color: '#E58621', fontFamily: "'Inter', sans-serif" }}>
                          Read Full Article
                          <svg
                            className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              </section>
            )}

            {/* ── Category Filters ───────────────────────────────────────── */}
            <section className="mb-10">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className="px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all duration-200"
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      background:
                        activeCategory === cat
                          ? 'linear-gradient(135deg, #E58621, #E0442B)'
                          : 'rgba(255,255,255,0.04)',
                      border:
                        activeCategory === cat
                          ? '1px solid transparent' :'1px solid rgba(255,255,255,0.1)',
                      color: activeCategory === cat ? '#fff' : 'rgba(255,255,255,0.55)',
                      backdropFilter: 'blur(10px)',
                    }}>
                    {cat}
                  </button>
                ))}
              </div>
            </section>

            {/* ── Stats Bar ─────────────────────────────────────────────── */}
            {!activeCategory === 'All' && (
              <div
                className="grid grid-cols-3 gap-4 mb-12 p-5 rounded-2xl"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  backdropFilter: 'blur(20px)',
                }}>
                {[
                  { value: `${posts.length}+`, label: 'Articles Published' },
                  { value: `${categories.length - 1}+`, label: 'Topics Covered' },
                  { value: 'Weekly', label: 'New Content' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <p
                      className="text-xl font-bold"
                      style={{ color: '#E58621', fontFamily: "'Space Grotesk', sans-serif" }}>
                      {stat.value}
                    </p>
                    <p
                      className="text-xs mt-0.5"
                      style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}>
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* ── Section Title ─────────────────────────────────────────── */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2
                  className="text-2xl font-bold text-white"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}>
                  {activeCategory === 'All' ?'Latest Articles'
                    : activeCategory}
                </h2>
                <p
                  className="text-sm mt-1"
                  style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}>
                  {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found
                </p>
              </div>
            </div>

            {/* ── Blog Grid ─────────────────────────────────────────────── */}
            {filteredPosts.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                  {paginatedPosts.map((post, i) => (
                    <BlogCard key={post.id} post={post} index={i} />
                  ))}
                </div>

                {/* ── Pagination ──────────────────────────────────────── */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mb-16">
                    {/* Prev button */}
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-80"
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.7)',
                        fontFamily: "'Space Grotesk', sans-serif",
                      }}>
                      ‹
                    </button>

                    {/* Page number buttons */}
                    {(() => {
                      const pages: (number | string)[] = [];
                      if (totalPages <= 7) {
                        for (let i = 1; i <= totalPages; i++) pages.push(i);
                      } else {
                        pages.push(1);
                        if (currentPage > 3) pages.push('...');
                        const start = Math.max(2, currentPage - 1);
                        const end = Math.min(totalPages - 1, currentPage + 1);
                        for (let i = start; i <= end; i++) pages.push(i);
                        if (currentPage < totalPages - 2) pages.push('...');
                        pages.push(totalPages);
                      }
                      return pages.map((page, idx) =>
                        page === '...' ? (
                          <span
                            key={`ellipsis-${idx}`}
                            className="w-9 h-9 flex items-center justify-center text-sm"
                            style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Space Grotesk', sans-serif" }}>
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page as number)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold transition-all duration-200 hover:opacity-80"
                            style={
                              currentPage === page
                                ? {
                                    background: 'linear-gradient(135deg, #E58621, #E0442B)',
                                    border: '1px solid transparent',
                                    color: '#fff',
                                    fontFamily: "'Space Grotesk', sans-serif",
                                  }
                                : {
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    color: 'rgba(255,255,255,0.7)',
                                    fontFamily: "'Space Grotesk', sans-serif",
                                  }
                            }>
                            {page}
                          </button>
                        )
                      );
                    })()}

                    {/* Next button */}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:opacity-80"
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        color: 'rgba(255,255,255,0.7)',
                        fontFamily: "'Space Grotesk', sans-serif",
                      }}>
                      ›
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-24">
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
                  style={{ background: 'rgba(229,134,33,0.1)', border: '1px solid rgba(229,134,33,0.2)' }}>
                  <svg
                    className="w-7 h-7"
                    style={{ color: '#E58621' }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                    />
                  </svg>
                </div>
                <h3
                  className="text-xl font-bold text-white mb-2"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  No articles found
                </h3>
                <p
                  className="text-sm"
                  style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}>
                  Try adjusting your search or browse all categories.
                </p>
                <button
                  onClick={() => {
                    setActiveCategory('All');
                  }}
                  className="mt-4 px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #E58621, #E0442B)', fontFamily: "'Inter', sans-serif" }}>
                  Clear Filters
                </button>
              </div>
            )}

            {/* ── Topics Grid ───────────────────────────────────────────── */}
            <section className="mb-16">
              <h2
                className="text-2xl font-bold text-white mb-6"
                style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}>
                Browse by Topic
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {categories
                  .filter((c) => c !== 'All')
                  .map((cat) => {
                    const count = posts.filter((p) => p.category === cat).length;
                    return (
                      <button
                        key={cat}
                        onClick={() => {
                          setActiveCategory(cat);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="group p-4 rounded-2xl text-left transition-all duration-200 hover:scale-[1.02]"
                        style={{
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          backdropFilter: 'blur(10px)',
                        }}>
                        <p
                          className="text-sm font-semibold text-white group-hover:text-[#E58621] transition-colors duration-200 mb-1"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          {cat}
                        </p>
                        <p
                          className="text-xs"
                          style={{ color: 'rgba(255,255,255,0.35)', fontFamily: "'Inter', sans-serif" }}>
                          {count} article{count !== 1 ? 's' : ''}
                        </p>
                      </button>
                    );
                  })}
              </div>
            </section>
          </>
        )}
      </main>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer
        className="relative border-t"
        style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.01)' }}>
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 h-px w-96"
          style={{ background: 'linear-gradient(to right, transparent, rgba(229,134,33,0.4), transparent)' }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-8 h-8 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #E58621, #E0442B)' }}>
                  <span
                    className="text-white font-bold text-sm"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    A
                  </span>
                </div>
                <span
                  className="font-bold text-lg text-white"
                  style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700 }}>
                  Amaze Insights
                </span>
              </div>
              <p
                className="text-sm leading-relaxed max-w-xs"
                style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}>
                Premium AI visual solutions for e-commerce agencies. White-label partnership delivering
                campaign-ready imagery.
              </p>
            </div>

            <div>
              <h4
                className="text-xs font-bold tracking-widest uppercase mb-5 text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Navigation
              </h4>
              <ul className="space-y-3">
                {[
                  { label: 'Home', href: '/' },
                  { label: 'Services', href: '/#services' },
                  { label: 'Our Work', href: '/#slider' },
                  { label: 'Blog', href: '/blog' },
                ].map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      className="text-sm transition-colors duration-200 hover:text-white"
                      style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4
                className="text-xs font-bold tracking-widest uppercase mb-5 text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Blog Topics
              </h4>
              <ul className="space-y-3">
                {categories
                  .filter((c) => c !== 'All')
                  .slice(0, 4)
                  .map((topic) => (
                    <li key={topic}>
                      <button
                        onClick={() => setActiveCategory(topic)}
                        className="text-sm transition-colors duration-200 hover:text-white text-left"
                        style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}>
                        {topic}
                      </button>
                    </li>
                  ))}
              </ul>
            </div>

            <div>
              <h4
                className="text-xs font-bold tracking-widest uppercase mb-5 text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Contact
              </h4>
              <ul className="space-y-3">
                {[
                  { label: 'Partnership Inquiries', href: 'https://contact.amaze.plus/' },
                  { label: '24-48h Delivery', href: 'https://contact.amaze.plus/' },
                  { label: 'Quality Guarantee', href: 'https://contact.amaze.plus/' },
                ].map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      className="text-sm transition-colors duration-200 hover:text-white"
                      style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Inter', sans-serif" }}>
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div
            className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-8"
            style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
            <p
              className="text-sm"
              style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Inter', sans-serif" }}>
              © 2025 Amaze. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                href="/privacy-policy"
                className="text-sm transition-colors duration-200 hover:text-white"
                style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Inter', sans-serif" }}>
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="text-sm transition-colors duration-200 hover:text-white"
                style={{ color: 'rgba(255,255,255,0.3)', fontFamily: "'Inter', sans-serif" }}>
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* ── Global Styles ─────────────────────────────────────────────────── */}
      <style jsx global>{`
        @keyframes glowPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.75; }
        }
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(24px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .blog-card {
          animation: fadeUp 0.5s ease-out forwards;
          opacity: 0;
        }
        .blog-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.06) !important;
          border-color: rgba(229,134,33,0.4) !important;
        }
        input[type='text']:focus,
        input[type='email']:focus {
          border-color: #E58621 !important;
          box-shadow: 0 0 0 3px rgba(229,134,33,0.15);
        }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #1a1a1a; }
        ::-webkit-scrollbar-thumb { background: rgba(229,134,33,0.3); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(229,134,33,0.5); }
      `}</style>
    </div>
  );
}